import React, { FormEvent, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckCircle2,
  Crown,
  Gift,
  Loader2,
  MessageSquare,
  ShieldCheck,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import { db } from '../firebase/config';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { logEvent } from '../utils/analytics';

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WAITLIST_BENEFITS = [
  {
    title: 'Early Access',
    description: 'Be the first to experience new features before they go public.',
    icon: Zap,
  },
  {
    title: 'Direct Influence',
    description: 'Share feedback directly with the development team.',
    icon: MessageSquare,
  },
  {
    title: 'Security & Privacy',
    description: 'Your health data is protected with physician-grade logic.',
    icon: ShieldCheck,
  },
  {
    title: 'Exclusive Perks',
    description: 'Founding users get special recognition and launch benefits.',
    icon: Gift,
  },
];

export default function WaitlistModal({ isOpen, onClose }: WaitlistModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errorMessage) setErrorMessage(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    const name = formData.name.trim();
    const email = formData.email.toLowerCase().trim();
    const phone = formData.phone.trim();

    if (!name || !email) {
      setErrorMessage('Please enter your name and email.');
      setIsSubmitting(false);
      return;
    }

    const waitlistRef = doc(db, 'waitlist', email);

    try {
      await setDoc(waitlistRef, {
        name,
        email,
        phone: phone || '',
        source: 'hero_waitlist_modal',
        status: 'waitlist',
        createdAt: serverTimestamp(),
      });

      logEvent('Conversion', 'Waitlist_Join_Success', 'Hero_Modal');
      setSubmitted(true);

      setTimeout(() => {
        onClose();
        setTimeout(() => {
          setSubmitted(false);
          setFormData({ name: '', email: '', phone: '' });
        }, 500);
      }, 3500);
    } catch (error: any) {
      if (error?.code === 'permission-denied') {
        setErrorMessage("You're already on the waitlist, or signups are unavailable right now.");
      } else if (error?.code === 'unavailable') {
        setErrorMessage('Network error. Please check your connection and try again.');
      } else {
        setErrorMessage('Something went wrong. Please try again in a moment.');
      }

      console.error('Firestore Error:', error?.code, error?.message);
      logEvent('Error', 'Waitlist_Join_Failed', error?.code || 'unknown');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-slate-950/75 backdrop-blur-md"
          onClick={onClose}
        >
          <div className="flex min-h-screen items-center justify-center p-4 md:p-6">
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-6xl overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,rgba(8,12,24,0.98),rgba(5,9,18,0.98))] shadow-[0_30px_120px_rgba(0,0,0,0.48)]"
              style={{ maxHeight: '90vh' }}
            >
              <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-[-8%] top-0 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
                <div className="absolute right-[-8%] bottom-0 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" />
              </div>

              <button
                type="button"
                onClick={onClose}
                className="absolute right-4 top-4 z-30 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/80 transition hover:bg-white/10 hover:text-white"
              >
                <X size={18} />
              </button>

              <div className="relative grid max-h-[90vh] grid-cols-1 overflow-y-auto no-scrollbar lg:grid-cols-[1fr_0.92fr]">
                {/* LEFT CONTENT */}
                <div className="border-b border-white/10 p-6 sm:p-8 lg:border-b-0 lg:border-r lg:p-10">
                  <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-[11px] font-black uppercase tracking-[0.22em] text-cyan-300">
                    <Sparkles size={14} />
                    Join the waitlist
                  </div>

                  <h2 className="mt-5 max-w-xl text-3xl font-black leading-tight text-white sm:text-4xl">
                    Join the LesslyKilo waitlist
                  </h2>

                  <p className="mt-4 max-w-xl text-base leading-7 text-white/70">
                    Get early access to LesslyKilo and help shape a simpler, smarter way to manage weight and
                    health progress.
                  </p>

                  <div className="mt-6 rounded-3xl border border-amber-400/20 bg-gradient-to-br from-amber-400/10 via-yellow-300/5 to-transparent p-5">
                    <div className="flex items-start gap-4">
                      <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-400/15 text-amber-300">
                        <Crown size={22} />
                      </div>
                      <div>
                        <div className="text-sm font-black uppercase tracking-[0.18em] text-amber-300">
                          Founding member reward
                        </div>
                        <div className="mt-2 text-xl font-black leading-snug text-white">
                          The first 100 users to join the waitlist get lifetime free premium access.
                        </div>
                        <p className="mt-2 text-sm leading-6 text-white/70">
                          Join early, secure your spot, and unlock premium forever at no cost when you are among
                          the first 100.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                    {WAITLIST_BENEFITS.map((benefit) => (
                      <div key={benefit.title} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <div className="flex items-start gap-4">
                          <div className="mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-cyan-400/10 text-cyan-300">
                            <benefit.icon size={20} />
                          </div>
                          <div>
                            <div className="text-base font-extrabold text-white">{benefit.title}</div>
                            <div className="mt-1 text-sm text-white/65">{benefit.description}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* RIGHT FORM */}
                <div className="bg-white/[0.01] p-6 sm:p-8 lg:p-10">
                  {!submitted ? (
                    <>
                      <div className="mb-6">
                        <h3 className="text-2xl font-black text-white">Reserve your spot</h3>
                        <p className="mt-2 text-sm text-white/65">
                          Join the waitlist now. We’ll let you know as soon as access opens.
                        </p>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                          <label className="mb-2 block text-sm font-bold text-white/85">Full name</label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Enter your name"
                            required
                            className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-white outline-none focus:border-cyan-400/40"
                          />
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-bold text-white/85">Email address</label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="you@example.com"
                            required
                            className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-white outline-none focus:border-cyan-400/40"
                          />
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-bold text-white/85">Phone (optional)</label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            placeholder="+267..."
                            className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-white outline-none focus:border-cyan-400/40"
                          />
                        </div>

                        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3">
                          <p className="text-sm font-semibold text-emerald-200">
                            First 100 users get lifetime free premium access.
                          </p>
                        </div>

                        {errorMessage && (
                          <div className="rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                            {errorMessage}
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-500 to-emerald-500 py-4 font-black text-white hover:brightness-110 disabled:opacity-50"
                        >
                          {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
                          {isSubmitting ? 'Joining waitlist...' : 'Join the waitlist'}
                        </button>
                      </form>
                    </>
                  ) : (
                    <div className="flex min-h-[400px] flex-col items-center justify-center text-center">
                      <CheckCircle2 size={48} className="text-emerald-400" />
                      <h3 className="mt-6 text-3xl font-black text-white">You’re on the list!</h3>
                      <p className="mt-3 max-w-md text-white/65">
                        Thanks for joining the LesslyKilo waitlist. Keep an eye on your inbox for early access
                        updates and launch news.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}